// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useDialogControl } from '@hooks/other/useDialogControl';
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { InfoOutlined, MoreVert } from '@mui/icons-material';
import LockIcon from '@mui/icons-material/Lock';
import { Box, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { Holding } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Holding';
import { ChoiceDialog } from '../../../components/ChoiceDialog';
import { HoldingDetail } from '../../../components/HoldingDetails';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import useHoldings from '../../../hooks/queries/registry/useHoldings';
import TemplateContract from '../../../utils/TemplateContract';
import { aggregateUnlockedHoldings } from '../../../utils/aggregateHoldings';
import { fmt, values } from '../../../utils/common';
import { PageWrapper } from '../../PageWrapper';
import { RequestBurnDialog } from '../burn/RequestBurnDialog';
import { RequestMintDialog } from '../mint/RequestMintDialog';
import { OfferTransferDialog } from '../transfer/OfferTransferDialog';
import { useCreateHoldingsActions } from './useCreateHoldingsActions';

type Row = TemplateContract<Holding>;

const createHeaders: () => GridColDef<Row>[] = () => {
  return [
    {
      field: 'instrument',
      headerName: 'Instrument',
      valueGetter: (params) => params.row.payload.instrument.id,
      flex: 150,
      filterable: true,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      valueGetter: (params) => Number(params.row.payload.amount),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Box>{fmt(params.row.payload.amount, 2)} </Box>
          {params.row.payload.lock && <LockIcon fontSize="small" sx={{ scale: 0.5 }} />}
        </Box>
      ),
      flex: 150,
      filterable: true,
    },
    {
      field: 'registrar',
      headerName: 'Registrar',
      valueGetter: (params) => params.row.payload.registrar,
      renderCell: (params) => <PartyDisplay party={params.row.payload.registrar} />,
      flex: 150,
      filterable: true,
    },
    {
      field: 'holder',
      headerName: 'Holder',
      renderCell: (params) => <PartyDisplay party={params.row.payload.owner} />,
      flex: 150,
      valueGetter: (params) => params.row.payload.owner,
      filterable: true,
    },
    {
      field: 'label',
      headerName: 'Label',
      valueGetter: (params) => params.row.payload.label,
      flex: 150,
      filterable: true,
      hide: true,
    },
    {
      field: 'lockedBy',
      headerName: 'Locked By',
      renderCell: (params) =>
        params.row.payload.lock
          ? values(params.row.payload.lock.lockers).map((party) => (
              <PartyDisplay key={uuidv4()} party={party} />
            ))
          : null,
      flex: 150,
    },
    {
      field: 'lockedContext',
      headerName: 'Lock Context',
      valueGetter: (params) => (params.row.payload.lock ? params.row.payload.lock.context : ''),
      flex: 150,
    },
    {
      field: 'action',
      headerName: '',
      align: 'right',
      renderCell: () => (
        <Box sx={{ cursor: 'pointer', display: 'flex' }}>
          <MoreVert />
        </Box>
      ),
      flex: 150,
      disableColumnMenu: true,
    },
  ];
};

interface HoldingsProps {
  disableVirtualization?: boolean;
}
export const Holdings: React.FC<HoldingsProps> = ({ disableVirtualization }) => {
  const holdings = useHoldings();
  const mergedHoldings = React.useMemo(
    () => aggregateUnlockedHoldings(holdings.data || []),
    [holdings.data],
  );
  const [selectedRow, setSelectedRow] = useState<TemplateContract<Holding> | undefined>(undefined);
  const onRowClick = (r: GridRowParams<TemplateContract<Holding>>) => {
    setSelectedRow(r.row);
  };
  const onClose = () => {
    setSelectedRow(undefined);
  };
  const { setDialogName, dialogName, onCloseDialog } = useDialogControl();
  const actions = useCreateHoldingsActions(onClose, selectedRow);
  const { isLoading } = holdings;
  if (isLoading) return <Loading />;
  const buttons = [
    {
      name: 'Offer transfer',
      onClick: () => setDialogName('transfer'),
    },
    {
      name: 'Request mint',
      onClick: () => setDialogName('mint'),
    },
    {
      name: 'Request burn',
      onClick: () => setDialogName('burn'),
    },
  ];
  return (
    <PageWrapper
      buttons={buttons}
      pageName={
        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1 }}>
          <Typography
            sx={{ textTransform: 'capitalize' }}
            fontWeight="bold"
            color="primary"
            variant="h4"
          >
            Holdings
          </Typography>
          <Tooltip title="Click on a holding row to show actions such as transfer and burn.">
            <InfoOutlined color="secondary" fontSize="small" sx={{ ml: 0.5 }} />
          </Tooltip>
        </Box>
      }
    >
      <ChoiceDialog
        title={selectedRow?.payload.instrument.id || ''}
        open={!!selectedRow}
        onClose={onClose}
        actions={actions}
      >
        <HoldingDetail row={selectedRow} />
      </ChoiceDialog>
      {dialogName === 'transfer' && <OfferTransferDialog open onClose={onCloseDialog} />}
      {dialogName === 'mint' && <RequestMintDialog open onClose={onCloseDialog} />}
      {dialogName === 'burn' && <RequestBurnDialog open onClose={onCloseDialog} />}

      <DataTable
        columns={createHeaders()}
        rows={mergedHoldings}
        rowKey={(r) => r.contractId}
        onRowClick={onRowClick}
        disableVirtualization={disableVirtualization}
      />
    </PageWrapper>
  );
};
